import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AppointmentRescheduleContext } from "@/components/appointments/AppointmentDialog";

export interface OpenAppointmentDraftOptions {
  initialDate?: Date;
  initialHour?: number;
  initialLeadId?: string;
  conversationId?: string | null;
  rescheduleContext?: AppointmentRescheduleContext;
  onSuccess?: () => void;
}

interface AppointmentDraftSession extends OpenAppointmentDraftOptions {
  key: number;
}

interface AppointmentDraftContextValue {
  isActive: boolean;
  minimized: boolean;
  setMinimized: (value: boolean) => void;
  session: AppointmentDraftSession | null;
  openDraft: (options: OpenAppointmentDraftOptions) => void;
  closeDraft: () => void;
}

const AppointmentDraftContext = createContext<AppointmentDraftContextValue | null>(null);

export function AppointmentDraftProvider({
  children,
  workspaceId,
}: {
  children: ReactNode;
  workspaceId: string | null;
}) {
  const [session, setSession] = useState<AppointmentDraftSession | null>(null);
  const [minimized, setMinimized] = useState(false);

  const openDraft = useCallback(
    (options: OpenAppointmentDraftOptions) => {
      if (!workspaceId) return;
      setSession({ ...options, key: Date.now() });
      setMinimized(false);
    },
    [workspaceId],
  );

  const closeDraft = useCallback(() => {
    setSession(null);
    setMinimized(false);
  }, []);

  const value = useMemo(
    () => ({
      isActive: session !== null,
      minimized,
      setMinimized,
      session,
      openDraft,
      closeDraft,
    }),
    [session, minimized, openDraft, closeDraft],
  );

  return (
    <AppointmentDraftContext.Provider value={value}>{children}</AppointmentDraftContext.Provider>
  );
}

export function useAppointmentDraft() {
  const ctx = useContext(AppointmentDraftContext);
  if (!ctx) {
    throw new Error("useAppointmentDraft must be used within AppointmentDraftProvider");
  }
  return ctx;
}

export function useAppointmentDraftOptional() {
  return useContext(AppointmentDraftContext);
}
